import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { addDoc, collection, deleteDoc, doc, getDocs, query, updateDoc, where } from 'firebase/firestore';
import { db } from '../firebase';
import { schoolClassSortOrder } from '../services/TimetableService';
import StudentIDCard from './StudentIDCard';
import './AdminStudentsView.css';
import './SuperAdminDetailPages.css';
import StatCards from './StatCards';

const SCHOOL_CODES = {
  'Beach Allen Primary School': 'BAP',
  'Bronte-Welsh Primary School': 'BWP',
  'Cayon Primary School': 'CPS',
  'Christ Church Nichola Town Primary School': 'CCN',
  'Deane-Glasford Primary School': 'DGP',
  'Dieppe Bay Primary School': 'DBP',
  'Dr. William Connor Primary School': 'DWC',
  'Edgar T. Morris Primary School': 'ETM',
  'Estridge Primary School': 'EPS',
  'Halliday-Smith Primary School': 'HSP',
  'Joshua Obadiah Williams Primary School': 'JOW',
  'Newton Ground Primary School': 'NGP',
  'Saddlers Primary School': 'SPS',
  'Sandy Point Primary School': 'SPP',
  "St. Paul's Primary School": 'SPP',
  'Tucker-Clarke Primary School': 'TCP',
  'Tyrell Williams Primary School': 'TWP',
  'Violet Petty Primary School': 'VPP',
  'Basseterre High School': 'BHS',
  'Cayon High School': 'CHS',
  'Charles E. Mills Secondary School': 'CEM',
  'Dr. Denzil L. Douglas Secondary School': 'DDS',
  'Verchilds High School': 'VHS',
  'Washington Archibald High School': 'WAH',
  'Clarence Fitzroy Bryant College (CFBC)': 'CFB',
  'UWI Open Campus - St. Kitts': 'UWI',
};

function generateStudentCode(schoolId) {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  const year = new Date().getFullYear();
  // Try to resolve school code from schoolId (Firestore UUID) or name
  // schoolId may be a Firestore doc ID or school name depending on how it was stored
  let schoolCode = 'UNK';
  if (schoolId && SCHOOL_CODES[schoolId]) {
    schoolCode = SCHOOL_CODES[schoolId];
  }
  return `${schoolCode}-${code}-${year}`;
}

function SuperAdminStudentsView({ adminData }) {
  const [saSchoolId, setSaSchoolId] = useState('');
  const [saSchools, setSaSchools] = useState([]);
  useEffect(() => {
    getDocs(collection(db, 'schools')).then(snap => setSaSchools(snap.docs.map(d => ({ id: d.id, ...d.data() })))).catch(() => {});
  }, []);
  adminData = useMemo(() => ({ ...(adminData || {}), schoolId: saSchoolId || adminData?.schoolId || null }), [adminData, saSchoolId]);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editStudent, setEditStudent] = useState(null);
  const [detailStudent, setDetailStudent] = useState(null);
  const [detailClassName, setDetailClassName] = useState('');
  const [detailClassInfo, setDetailClassInfo] = useState(null);
  const [allParents, setAllParents] = useState([]);
  const [originalClassId, setOriginalClassId] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(true);
  const [showIDCard, setShowIDCard] = useState(null);

  const [newStudent, setNewStudent] = useState({
    firstName: '',
    lastName: '',
    classId: '',
    birthDate: ''
  });

  const loadClasses = useCallback(async () => {
    console.log('🔥 AdminStudents: Loading classes, schoolId:', adminData?.schoolId);
    try {
      const snapshot = await getDocs(collection(db, 'classes'));
      let classesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      // Super Admin: no school selected = national view, load all classes
      const sid = (adminData?.schoolId || '').toLowerCase();
      if (sid) {
        classesData = classesData.filter(c => (c.schoolId || '').toLowerCase() === sid);
      }
      classesData.sort(schoolClassSortOrder);
      console.log('🔥 AdminStudents: Classes loaded', classesData.length, classesData);
      setClasses(classesData);
    } catch (error) {
      console.error('🔥 AdminStudents: Error loading classes:', error);
    }
  }, [adminData]);

  const loadStudents = useCallback(async () => {
    console.log('🔥 AdminStudents: Loading students');
    try {
      const studentsData = [];
      for (const cls of classes) {
        const classStudentsQuery = query(
          collection(db, 'classes', cls.id, 'students'),
          where('isActive', '==', true)
        );
        const snapshot = await getDocs(classStudentsQuery);
        snapshot.docs.forEach(doc => {
          studentsData.push({ id: doc.id, ...doc.data() });
        });
      }
      console.log('🔥 AdminStudents: Students loaded', studentsData.length, studentsData);
      setStudents(studentsData);
    } catch (error) {
      console.error('🔥 AdminStudents: Error loading students:', error);
    } finally {
      setLoading(false);
    }
  }, [classes]);

  useEffect(() => {
    loadClasses();
  }, [loadClasses]);

  useEffect(() => {
    if (classes.length > 0) {
      loadStudents();
    }
  }, [classes, loadStudents]);

  // Load parents (parents collection merged with user docs) so the student
  // detail page can resolve parent info from linkedChildrenIds
  useEffect(() => {
    console.log('🔥 AdminStudents: Loading parents for detail resolution');
    (async () => {
      try {
        const [parentsSnap, usersSnap] = await Promise.all([
          getDocs(collection(db, 'parents')),
          getDocs(collection(db, 'users'))
        ]);
        const usersById = {};
        usersSnap.docs.forEach(d => { usersById[d.id] = d.data(); });
        const merged = parentsSnap.docs.map(d => ({
          ...usersById[d.id],
          ...d.data(),
          id: d.id,
          linkedChildrenIds: d.data().linkedChildrenIds || (usersById[d.id] && usersById[d.id].linkedChildrenIds) || []
        }));
        // Also include Parent users that have no parents-collection doc
        usersSnap.docs.forEach(d => {
          const u = d.data();
          if (u.role === 'Parent' && !merged.find(p => p.id === d.id)) {
            merged.push({ id: d.id, ...u });
          }
        });
        console.log('🔥 AdminStudents: Parents loaded for resolution', merged.length);
        setAllParents(merged);
      } catch (error) {
        console.error('🔥 AdminStudents: Error loading parents:', error);
      }
    })();
  }, []);

  // Resolve schoolId -> school name (match doc id, uuid field, or name; case-insensitive)
  const resolveSchoolName = (schoolId) => {
    if (!schoolId) return 'N/A';
    const sid = String(schoolId).toLowerCase();
    const match = saSchools.find(s =>
      String(s.id).toLowerCase() === sid ||
      String(s.uuid || '').toLowerCase() === sid ||
      String(s.name || '').toLowerCase() === sid
    );
    return match ? (match.name || match.id) : String(schoolId);
  };

  // Resolve the parent(s) linked to a student via linkedChildrenIds matching
  // student.studentId, document id, or uuid (case-insensitive)
  const getStudentParents = (student) => {
    if (!student) return [];
    const ids = [student.studentId, student.id, student.uuid]
      .filter(Boolean)
      .map(x => String(x).toLowerCase());
    return allParents.filter(p =>
      (p.linkedChildrenIds || []).map(x => String(x).toLowerCase()).some(id => ids.includes(id))
    );
  };

  const getFilteredStudents = () => {
    if (!searchText) {
      return students;
    }
    return students.filter(student =>
      student.fullName.toLowerCase().includes(searchText.toLowerCase()) ||
      student.studentId.toLowerCase().includes(searchText.toLowerCase()) ||
      (student.email && student.email.toLowerCase().includes(searchText.toLowerCase()))
    );
  };

  const getStudentsByClass = () => {
    const filtered = getFilteredStudents();
    const grouped = {};
    classes.forEach(cls => {
      grouped[cls.id] = {
        class: cls,
        students: filtered.filter(s => s.classId === cls.id)
      };
    });
    return Object.values(grouped).filter(g => g.students.length > 0);
  };

  const handleAddStudent = async (e) => {
    e.preventDefault();
    try {
      const birthDate = new Date(newStudent.birthDate);
      const age = new Date().getFullYear() - birthDate.getFullYear();
      const studentId = generateStudentCode(adminData?.schoolId);
      
      await addDoc(collection(db, 'classes', newStudent.classId, 'students'), {
        ...newStudent,
        studentId,
        fullName: `${newStudent.firstName} ${newStudent.lastName}`,
        birthDate: birthDate.toISOString(),
        age: age,
        isActive: true,
        createdAt: new Date().toISOString(),
        schoolId: adminData?.schoolId || null
      });
      setShowAddForm(false);
      setNewStudent({ firstName: '', lastName: '', classId: '', birthDate: '' });
      loadStudents();
    } catch (error) {
      console.error('Error adding student:', error);
      alert('Failed to add student');
    }
  };

  const handleDeleteStudent = async (student) => {
    const confirmed = window.confirm(
      `Are you sure you want to PERMANENTLY DELETE ${student.fullName}?\n\nThis action cannot be undone. All grades, attendance records, and assignments for this student will remain in the system but the student record will be removed.`
    );
    if (!confirmed) return false;
    try {
      await deleteDoc(doc(db, 'classes', student.classId, 'students', student.id));
      loadStudents();
      return true;
    } catch (error) {
      console.error('Error deleting student:', error);
      alert('Failed to delete student');
      return false;
    }
  };

  const handleEditClick = (student) => {
    const birthDate = student.birthDate ? new Date(student.birthDate).toISOString().split('T')[0] : '';
    setEditStudent({
      ...student,
      birthDate
    });
    setOriginalClassId(student.classId);
    setIsEditing(true);
  };

  const handleCloseModal = () => {
    setIsEditing(false);
    setEditStudent(null);
    setOriginalClassId(null);
  };

  const handleUpdateStudent = async (e) => {
    e.preventDefault();
    if (!editStudent) return;
    try {
      const birthDate = new Date(editStudent.birthDate);
      const age = new Date().getFullYear() - birthDate.getFullYear();
      const newClassId = editStudent.classId;

      const studentData = {
        firstName: editStudent.firstName,
        lastName: editStudent.lastName,
        fullName: `${editStudent.firstName} ${editStudent.lastName}`,
        studentId: editStudent.studentId,
        birthDate: birthDate.toISOString(),
        age: age,
        classId: newClassId,
        updatedAt: new Date().toISOString()
      };

      if (originalClassId && originalClassId !== newClassId) {
        // Moving student to a different class — delete old doc, create new one
        await deleteDoc(doc(db, 'classes', originalClassId, 'students', editStudent.id));
        const newStudentRef = await addDoc(collection(db, 'classes', newClassId, 'students'), {
          ...studentData,
          isActive: true,
          createdAt: new Date().toISOString(),
          schoolId: adminData?.schoolId || null
        });
        console.log('Student moved to new class with id:', newStudentRef.id);
      } else {
        await updateDoc(doc(db, 'classes', newClassId, 'students', editStudent.id), studentData);
      }

      handleCloseModal();
      loadStudents();
    } catch (error) {
      console.error('Error updating student:', error);
      alert('Failed to update student');
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  const studentsByClass = getStudentsByClass();

  // Full-screen student detail page — replaces the list (not an overlay)
  if (detailStudent) {
    const s = detailStudent;
    const linkedParents = getStudentParents(s);
    return (
      <div className="detail-page">
        <button className="dp-back" onClick={() => setDetailStudent(null)}>
          ◄ Back to Students
        </button>

        <div className="dp-hero">
          <div className="dp-avatar">
            {s.photoURL || s.photoUrl
              ? <img src={s.photoURL || s.photoUrl} alt="" />
              : (s.firstName ? s.firstName[0].toUpperCase() : 'S')}
          </div>
          <div className="dp-hero-text">
            <h2>{s.fullName || `${s.firstName || ''} ${s.lastName || ''}`.trim() || 'Unknown'}</h2>
            <p>Student ID: {s.studentId || 'N/A'}</p>
            <span className={`dp-badge ${s.isActive === false ? 'red' : 'green'}`}>
              {s.isActive === false ? 'Inactive' : 'Active'}
            </span>
          </div>
        </div>

        <div className="dp-card">
          <h3>Student Information</h3>
          <div className="dp-grid">
            <div className="dp-field">
              <div className="dp-label">Name</div>
              <div className="dp-value">{s.fullName || `${s.firstName || ''} ${s.lastName || ''}`.trim() || 'N/A'}</div>
            </div>
            <div className="dp-field">
              <div className="dp-label">Student ID</div>
              <div className="dp-value">{s.studentId || 'N/A'}</div>
            </div>
            <div className="dp-field">
              <div className="dp-label">Class</div>
              <div className="dp-value">{detailClassName || 'N/A'}</div>
            </div>
            <div className="dp-field">
              <div className="dp-label">Grade</div>
              <div className="dp-value">{(detailClassInfo && detailClassInfo.grade) || s.grade || 'N/A'}</div>
            </div>
            <div className="dp-field">
              <div className="dp-label">School</div>
              <div className="dp-value">{resolveSchoolName(s.schoolId || (detailClassInfo && detailClassInfo.schoolId))}</div>
            </div>
            <div className="dp-field">
              <div className="dp-label">Date of Birth</div>
              <div className="dp-value">{s.birthDate ? new Date(s.birthDate).toLocaleDateString() : 'N/A'}</div>
            </div>
            <div className="dp-field">
              <div className="dp-label">Age</div>
              <div className="dp-value">{s.age != null ? s.age : 'N/A'}</div>
            </div>
            <div className="dp-field">
              <div className="dp-label">Email</div>
              <div className="dp-value">{s.email || 'N/A'}</div>
            </div>
            <div className="dp-field">
              <div className="dp-label">Enrollment Status</div>
              <div className="dp-value">{s.isActive === false ? 'Inactive' : 'Active'}</div>
            </div>
            <div className="dp-field">
              <div className="dp-label">Created</div>
              <div className="dp-value">{s.createdAt ? new Date(s.createdAt).toLocaleDateString() : 'N/A'}</div>
            </div>
          </div>
        </div>

        <div className="dp-card">
          <h3>Parent / Guardian ({linkedParents.length})</h3>
          {linkedParents.length === 0 ? (
            <p className="dp-empty">No linked parent found</p>
          ) : (
            linkedParents.map(p => (
              <div key={p.id} className="dp-row">
                <span className="dp-row-main">
                  {p.fullName || `${p.firstName || ''} ${p.lastName || ''}`.trim() || p.username || 'Unknown'}
                </span>
                <span className="dp-row-sub">
                  {[p.email, p.phoneNumber].filter(Boolean).join(' • ') || 'No contact info'}
                </span>
              </div>
            ))
          )}
        </div>

        <div className="dp-actions">
          <button
            type="button"
            className="dp-btn-info"
            onClick={() => {
              const student = s;
              const cls = detailClassInfo;
              setDetailStudent(null);
              setShowIDCard({ ...student, className: (cls && cls.name) || detailClassName, grade: cls && cls.grade });
            }}
          >
            🆔 Student ID Card
          </button>
          <button
            type="button"
            className="dp-btn-primary"
            onClick={() => { const student = s; setDetailStudent(null); handleEditClick(student); }}
          >
            Edit
          </button>
          <button
            type="button"
            className="dp-btn-danger"
            onClick={async () => { if (await handleDeleteStudent(s)) setDetailStudent(null); }}
          >
            Delete
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-students-view">
      <div className="view-header">
        <h2>Students</h2>
        <div className="sa-school-filter"><label>School:</label><select value={saSchoolId} onChange={(e) => setSaSchoolId(e.target.value)}><option value="">All Schools</option>{saSchools.map(s => <option key={s.id} value={s.id}>{s.name || s.id}</option>)}</select></div>
        <button className="add-button" onClick={() => setShowAddForm(true)}>
          + Add Student
        </button>
      </div>
      <StatCards items={[['Students',students.length],['Classes',classes.length],['Active Classes',new Set(students.map(s => s.classId)).size],['Filtered',getFilteredStudents().length]]} />

      <div className="search-bar">
        <input
          type="text"
          placeholder="Search students..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </div>

      <p className="count-text">Students: {getFilteredStudents().length} in {studentsByClass.length} classes</p>

      {showAddForm && (
        <div className="add-student-form">
          <h3>Create New Student</h3>
          <form onSubmit={handleAddStudent}>
            <div className="form-row">
              <div className="form-group">
                <label>First Name *</label>
                <input
                  type="text"
                  value={newStudent.firstName}
                  onChange={(e) => setNewStudent({ ...newStudent, firstName: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Last Name *</label>
                <input
                  type="text"
                  value={newStudent.lastName}
                  onChange={(e) => setNewStudent({ ...newStudent, lastName: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Student ID</label>
                <input
                  type="text"
                  value="Auto-generated"
                  disabled
                  style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                />
                <small style={{ color: '#6b7280' }}>Format: [SCHOOL_CODE]-[CODE]-[YEAR]</small>
              </div>
              <div className="form-group">
                <label>Class *</label>
                <select
                  value={newStudent.classId}
                  onChange={(e) => setNewStudent({ ...newStudent, classId: e.target.value })}
                  required
                >
                  <option value="">Select Class</option>
                  {classes.map(cls => (
                    <option key={cls.id} value={cls.id}>
                      {cls.name} ({cls.grade})
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Birth Date *</label>
              <input
                type="date"
                value={newStudent.birthDate}
                onChange={(e) => setNewStudent({ ...newStudent, birthDate: e.target.value })}
                required
              />
            </div>
            <div className="form-actions">
              <button type="button" onClick={() => setShowAddForm(false)}>
                Cancel
              </button>
              <button type="submit">Create Student</button>
            </div>
          </form>
        </div>
      )}

      {studentsByClass.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">👥</div>
          <h3>No Students</h3>
          <p>Add students to get started</p>
        </div>
      ) : (
        <div className="students-list">
          {studentsByClass.map(({ class: cls, students: classStudents }) => (
            <div key={cls.id} className="class-section">
              <div className="class-section-header">
                <h4>{cls.name}</h4>
                <span>Grade: {cls.grade} • {classStudents.length} students</span>
              </div>
              {classStudents.map(student => (
                <div key={student.id} className="student-card">
                  <div className="student-avatar" onClick={() => { setDetailStudent(student); setDetailClassName(cls.name); setDetailClassInfo(cls); }}>
                    {student.firstName ? student.firstName[0] : 'S'}
                  </div>
                  <div className="student-info" onClick={() => { setDetailStudent(student); setDetailClassName(cls.name); setDetailClassInfo(cls); }}>
                    <h5>{student.fullName || `${student.firstName || ''} ${student.lastName || ''}`.trim() || 'Unknown'}</h5>
                    <p>ID: {student.studentId}</p>
                    <p>Class: {cls.name}</p>
                    {student.email && <p>Email: {student.email}</p>}
                  </div>
                  <div className="student-status">
                    <span className="status-dot active"></span>
                  </div>
                  <div className="student-actions">
                    <button
                      className="id-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowIDCard({ ...student, className: cls.name, grade: cls.grade });
                      }}
                      title="Print ID Card"
                    >
                      🆔
                    </button>
                    <button
                      className="delete-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteStudent(student);
                      }}
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {isEditing && editStudent && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Edit Student</h3>
            <form onSubmit={handleUpdateStudent}>
              <div className="form-row">
                <div className="form-group">
                  <label>First Name *</label>
                  <input
                    type="text"
                    value={editStudent.firstName}
                    onChange={(e) => setEditStudent({ ...editStudent, firstName: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input
                    type="text"
                    value={editStudent.lastName}
                    onChange={(e) => setEditStudent({ ...editStudent, lastName: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Student ID *</label>
                  <input
                    type="text"
                    value={editStudent.studentId}
                    disabled
                    style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                  />
                </div>
                <div className="form-group">
                  <label>Class *</label>
                  <select
                    value={editStudent.classId}
                    onChange={(e) => setEditStudent({ ...editStudent, classId: e.target.value })}
                    required
                  >
                    <option value="">Select Class</option>
                    {classes.map(cls => (
                      <option key={cls.id} value={cls.id}>
                        {cls.name} ({cls.grade})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Birth Date *</label>
                <input
                  type="date"
                  value={editStudent.birthDate}
                  onChange={(e) => setEditStudent({ ...editStudent, birthDate: e.target.value })}
                  required
                />
              </div>
              <div className="form-actions">
                <button type="button" className="cancel-button" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button type="submit" className="save-button">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showIDCard && (
        <StudentIDCard
          student={showIDCard}
          schoolName={adminData?.schoolName || 'KN Education Portal'}
          onClose={() => setShowIDCard(null)}
        />
      )}
    </div>
  );
}

export default SuperAdminStudentsView;
